import { useAutoneTranslation } from '@autone/translations';
import { useMemo } from 'react';
import { type Get } from 'type-fest';

import { type APP_NAME } from '../../consts';
import { type TableNamesWithAnyFilters } from '../../features/assortment-filters/types';
import {
  type AssortmentSessionStorageAtomData,
  useAssortmentSessionStorageAtom,
} from '../../storage/session-storage';

import { type AssortmentAdvancedFiltersTranslationsMap } from './useAssortmentAdvancedFilters.types';
import {
  getAdvancedFilterFieldTranslations,
  getAdvancedFilterOperatorTranslations,
} from './utilities/translations';

export const useAssortmentAdvancedFilters = <
  T extends TableNamesWithAnyFilters,
>(
  tableName: T,
) => {
  const sessionStorageKey = `${tableName}.advancedFilters` as const;

  /**
   * Typescript still needs help to narrow down the correct types for `selectedAdvancedFiltersApi`
   * because of the `.map` operation on `conditions` merging the unions.
   */
  type TableAdvancedFilterConditions = NonNullable<
    Get<AssortmentSessionStorageAtomData, typeof sessionStorageKey>
  >['conditions'];

  const { t, common } = useAutoneTranslation<typeof APP_NAME>();
  const [selectedAdvancedFilters, setSelectedAdvancedFilters] =
    useAssortmentSessionStorageAtom(sessionStorageKey);

  const translatedAdvancedFilterOptions: AssortmentAdvancedFiltersTranslationsMap =
    useMemo(
      () => ({
        fields: getAdvancedFilterFieldTranslations(t),
        operators: getAdvancedFilterOperatorTranslations(common),
      }),
      [t, common],
    );

  const selectedAdvancedFiltersApi = useMemo(() => {
    const { conditions, conditionCombineMode } = selectedAdvancedFilters || {};
    const shouldSubmitAdvancedFilters = conditions && conditions.length > 0;

    if (!shouldSubmitAdvancedFilters) {
      return undefined;
    } else {
      return {
        conditions: conditions.map(({ field, operator, value }) => ({
          field: field,
          operator: operator,
          value,
        })) as TableAdvancedFilterConditions,
        condition_combine_mode: conditionCombineMode,
      };
    }
  }, [selectedAdvancedFilters]);

  const selectedAdvancedFiltersCount =
    selectedAdvancedFilters?.conditions.length ?? 0;

  // if filter is not available in sessionStorage, return null
  return useMemo(() => {
    if (selectedAdvancedFilters === undefined) {
      return {
        selectedAdvancedFilters: null,
        selectedAdvancedFiltersCount: 0,
        selectedAdvancedFiltersApi: undefined,
        setSelectedAdvancedFilters,
        translatedAdvancedFilterOptions,
      };
    }
    return {
      selectedAdvancedFilters,
      selectedAdvancedFiltersCount,
      selectedAdvancedFiltersApi,
      setSelectedAdvancedFilters,
      translatedAdvancedFilterOptions,
    };
  }, [
    selectedAdvancedFilters,
    selectedAdvancedFiltersCount,
    selectedAdvancedFiltersApi,
    setSelectedAdvancedFilters,
    translatedAdvancedFilterOptions,
  ]);
};
